<template>
  <div class="dynamic-questionnaire-form" :class="{ 'readonly-form': readonly }">
    <van-cell-group v-if="fields.length">
      <div v-for="field in fields" :key="field.key" class="field-block">
        <!-- input / textarea / mobile / number：标签在上、输入在下，参考金数据布局 -->
        <div v-if="field.type === 'input'" class="field-text-block">
          <div class="field-label-row">
            <span class="field-label">{{ fieldLabel(field) }}</span>
          </div>
          <div v-if="field.description" class="field-desc">{{ field.description }}</div>
          <van-field
            v-model="localContent[field.key]"
            :placeholder="field.placeholder"
            :readonly="readonly"
            input-align="left"
            class="field-input-inner"
          />
        </div>
        <div v-else-if="field.type === 'textarea'" class="field-text-block">
          <div class="field-label-row">
            <span class="field-label">{{ fieldLabel(field) }}</span>
          </div>
          <div v-if="field.description" class="field-desc">{{ field.description }}</div>
          <van-field
            v-model="localContent[field.key]"
            :placeholder="field.placeholder"
            :readonly="readonly"
            type="textarea"
            rows="3"
            autosize
            input-align="left"
            class="field-input-inner"
          />
        </div>
        <div v-else-if="field.type === 'mobile'" class="field-text-block">
          <div class="field-label-row">
            <span class="field-label">{{ fieldLabel(field) }}</span>
          </div>
          <div v-if="field.description" class="field-desc">{{ field.description }}</div>
          <van-field
            v-model="localContent[field.key]"
            :placeholder="field.placeholder"
            :readonly="readonly"
            type="tel"
            input-align="left"
            class="field-input-inner"
          />
        </div>
        <div v-else-if="field.type === 'number'" class="field-text-block">
          <div class="field-label-row">
            <span class="field-label">{{ fieldLabel(field) }}</span>
          </div>
          <div v-if="field.description" class="field-desc">{{ field.description }}</div>
          <van-field
            v-model="localContent[field.key]"
            :placeholder="field.placeholder"
            :readonly="readonly"
            type="number"
            :min="field.min"
            :max="field.max"
            input-align="left"
            class="field-input-inner"
          />
        </div>
        <!-- choice: single (inline) -->
        <div v-else-if="isChoiceSingle(field)" class="field-choice-multi">
          <div class="field-label-row">
            <span class="field-label">{{ fieldLabel(field) }}</span>
          </div>
          <div v-if="field.description" class="field-desc">{{ field.description }}</div>
          <div v-for="opt in (field.options || [])" :key="opt.value" class="choice-option-row">
            <van-cell
              clickable
              :class="{ 'cell-readonly': readonly }"
              @click="!readonly && setChoiceSelected(field, [opt.value])"
            >
              <template #title>
                <div class="radio-option-row">
                  <span
                    class="radio-dot"
                    :class="{ 'radio-dot--selected': isOptionSelected(field, opt.value) }"
                  ></span>
                  <span>{{ opt.label }}</span>
                </div>
              </template>
            </van-cell>
            <van-field
              v-if="opt.allowInput && isOptionSelected(field, opt.value)"
              :value="getChoiceExtraInput(field, opt.value)"
              :placeholder="opt.inputPlaceholder || '请填写'"
              :readonly="readonly"
              size="small"
              class="choice-extra-input"
              @input="(val) => setChoiceExtraInput(field, opt.value, val)"
            />
          </div>
        </div>
        <!-- choice: multi (checkbox list) -->
        <div v-else-if="isChoiceMulti(field)" class="field-choice-multi">
          <div class="field-label-row">
            <span class="field-label">{{ fieldLabel(field) }}</span>
          </div>
          <div v-if="field.description" class="field-desc">{{ field.description }}</div>
          <div v-for="opt in (field.options || [])" :key="opt.value" class="choice-option-row">
            <van-cell
              clickable
              :class="{ 'cell-readonly': readonly }"
              @click="!readonly && toggleChoiceOption(field, opt)"
            >
              <template #title>
                <div class="radio-option-row">
                  <van-checkbox
                    :value="isOptionSelected(field, opt.value)"
                    :disabled="readonly"
                    @click.stop
                  >
                    {{ opt.label }}
                  </van-checkbox>
                </div>
              </template>
            </van-cell>
            <van-field
              v-if="opt.allowInput && isOptionSelected(field, opt.value)"
              :value="getChoiceExtraInput(field, opt.value)"
              :placeholder="opt.inputPlaceholder || '请填写'"
              :readonly="readonly"
              size="small"
              class="choice-extra-input"
              @input="(val) => setChoiceExtraInput(field, opt.value, val)"
            />
          </div>
        </div>
        <!-- fileUpload -->
        <div v-else-if="field.type === 'fileUpload'" class="field-file-upload">
          <div class="field-label-row">
            <span class="field-label">{{ fieldLabel(field) }}</span>
          </div>
          <div v-if="field.description" class="field-desc">{{ field.description }}</div>
          <template v-if="readonly">
            <div class="file-readonly-list">
              <template v-for="(item, idx) in normalizeAttachments(localContent[field.key])">
                <a
                  v-if="isPdfUrl(item.url)"
                  :key="idx"
                  :href="fullUrl(item.url)"
                  :download="`file-${idx}.pdf`"
                  class="file-link"
                >下载 {{ idx + 1 }}</a>
                <img
                  v-else
                  :key="idx"
                  :src="thumbUrl(item.url)"
                  :alt="`附件 ${idx + 1}`"
                  class="file-thumb"
                  @click="previewImage(field, idx)"
                />
              </template>
              <span v-if="!normalizeAttachments(localContent[field.key]).length" class="text-muted">-</span>
            </div>
          </template>
          <template v-else>
            <div class="upload-section">
              <van-uploader
                :file-list="getFileList(field.key)"
                :max-count="field.maxCount || 9"
                :accept="acceptForField(field)"
                :after-read="(file) => afterRead(file, field)"
                @delete="(file, detail) => onDelete(file, detail, field)"
              >
                <div class="upload-placeholder">
                  <van-icon name="plus" class="plus-icon" />
                  <span class="upload-text">添加文件</span>
                </div>
              </van-uploader>
            </div>
          </template>
        </div>
        <div v-if="field.description && field.type !== 'fileUpload' && !isChoiceMulti(field) && !['input','textarea','mobile','number'].includes(field.type)" class="field-desc-inline">{{ field.description }}</div>
      </div>
    </van-cell-group>
    <van-empty v-else description="暂无表单项" />
  </div>
</template>

<script>
import Vue from 'vue'
import { CellGroup, Field, Uploader, Icon, Toast, Checkbox } from 'vant'
import { getQiniuUploadToken, uploadImage } from '@/api/qiniu'
import { QNImageUtil } from '@/utils'
import { ConstConfig } from '@/constants'

Vue.use(CellGroup).use(Field).use(Uploader).use(Icon).use(Toast).use(Checkbox)

export default {
  name: 'DynamicQuestionnaireForm',
  props: {
    formConfig: { type: Object, default: () => ({}) },
    value: { type: Object, default: () => ({}) },
    readonly: { type: Boolean, default: false }
  },
  data() {
    return {
      localContent: {},
      fileListMap: {},
      qiniuToken: '',
    }
  },
  computed: {
    fields() {
      const c = this.formConfig
      if (!c || !Array.isArray(c.fields)) return []
      return c.fields
    }
  },
  watch: {
    value: {
      handler(val) {
        const raw = val && typeof val === 'object' ? { ...val } : {}
        this.localContent = this.normalizeContentForChoice(raw)
        this.syncFileListMap()
      },
      immediate: true
    }
  },
  mounted() {
    if (!this.readonly) {
      this.fetchQiniuToken()
    }
  },
  methods: {
    fieldLabel(field) {
      const base = field.required ? `* ${field.label}` : field.label
      if (this.isChoiceMulti(field)) {
        return `${base} (多选)`
      }
      return base
    },
    isChoiceSingle(field) {
      return field.type === 'choice' && field.mode === 'single'
    },
    isChoiceMulti(field) {
      return field.type === 'choice' && field.mode === 'multi'
    },
    getChoiceObj(field) {
      const val = this.localContent[field.key]
      if (val && typeof val === 'object' && Array.isArray(val.selected)) {
        return val
      }
      return { selected: [], extraInputMap: {} }
    },
    getChoiceSelected(field) {
      return this.getChoiceObj(field).selected || []
    },
    setChoiceSelected(field, selected) {
      const obj = this.getChoiceObj(field)
      const extraInputMap = { ...(obj.extraInputMap || {}) }
      Object.keys(extraInputMap).forEach(k => {
        if (!selected.includes(k)) delete extraInputMap[k]
      })
      this.$set(this.localContent, field.key, { selected: [...selected], extraInputMap })
    },
    getChoiceExtraInput(field, optionValue) {
      return this.getChoiceObj(field).extraInputMap?.[optionValue] ?? ''
    },
    setChoiceExtraInput(field, optionValue, val) {
      const obj = this.getChoiceObj(field)
      const extraInputMap = { ...(obj.extraInputMap || {}) }
      if (val != null && val !== '') {
        extraInputMap[optionValue] = String(val)
      } else {
        delete extraInputMap[optionValue]
      }
      this.$set(this.localContent, field.key, { ...obj, extraInputMap })
    },
    isOptionSelected(field, optionValue) {
      return this.getChoiceSelected(field).includes(optionValue)
    },
    toggleChoiceOption(field, opt) {
      const selected = [...this.getChoiceSelected(field)]
      const idx = selected.indexOf(opt.value)
      if (idx >= 0) {
        selected.splice(idx, 1)
      } else {
        selected.push(opt.value)
        if (field.mode === 'single') selected.length = 1
      }
      this.setChoiceSelected(field, selected)
    },
    normalizeContentForChoice(content) {
      const out = { ...content }
      const fields = this.fields || []
      fields.forEach(f => {
        if (!out[f.key]) return
        const val = out[f.key]
        if (f.type === 'choice' && (f.mode === 'single' || f.mode === 'multi')) {
          if (val && typeof val === 'object' && Array.isArray(val.selected)) {
            out[f.key] = { selected: val.selected, extraInputMap: val.extraInputMap || {} }
          } else if (val != null && val !== '') {
            out[f.key] = { selected: [String(val)], extraInputMap: {} }
          }
        }
      })
      return out
    },
    // 暴露给父组件调用，用于提交/保存时获取最新内容
    getContent() {
      return { ...this.localContent }
    },
    normalizeAttachments(val) {
      if (!val) return []
      const arr = Array.isArray(val) ? val : [val]
      return arr
        .map(item => (typeof item === 'string' ? { url: item } : { url: item && item.url }))
        .filter(x => x && x.url)
    },
    isPdfUrl(url) {
      if (!url) return false
      return String(url).toLowerCase().endsWith('.pdf')
    },
    thumbUrl(url) {
      return QNImageUtil.imageView(url, 120, 120)
    },
    fullUrl(url) {
      if (!url) return '#'
      if (url.match(/^(http|https):/i)) return url
      return ConstConfig.qiniuCdn + '/' + url
    },
    getFileList(key) {
      return this.fileListMap[key] || []
    },
    syncFileListMap() {
      this.fields.forEach(f => {
        if (f.type === 'fileUpload') {
          const items = this.normalizeAttachments(this.localContent[f.key])
          const list = items.map(item => ({
            url: this.fullUrl(item.url),
            isImage: !this.isPdfUrl(item.url)
          }))
          this.$set(this.fileListMap, f.key, list)
        }
      })
    },
    acceptForField(field) {
      const formats = field.fileFormat || ['jpg', 'jpeg', 'png', 'pdf']
      const mimeMap = {
        jpg: 'image/jpeg',
        jpeg: 'image/jpeg',
        png: 'image/png',
        gif: 'image/gif',
        pdf: 'application/pdf'
      }
      const accept = formats.map(f => mimeMap[f.toLowerCase()] || `*/${f}`).filter(Boolean)
      return accept.length ? accept.join(',') : 'image/*,application/pdf'
    },
    fetchQiniuToken() {
      getQiniuUploadToken().then(res => {
        const token = res && (res.data && res.data.token ? res.data.token : (res.token || res.data))
        if (token) this.qiniuToken = token
      }).catch(() => {})
    },
    afterRead(file, field) {
      if (!this.qiniuToken) {
        Toast('上传服务未就绪，请稍后重试')
        return
      }
      const formData = new FormData()
      formData.append('token', this.qiniuToken)
      formData.append('file', file.file)
      uploadImage(formData).then(res => {
        const hash = res.hash || res
        const url = typeof hash === 'string' ? hash : (hash.url || hash.key)
        const current = this.localContent[field.key]
        const arr = current ? (Array.isArray(current) ? [...current] : [current]) : []
        const newItem = typeof url === 'string' ? { url } : url
        arr.push(newItem)
        this.$set(this.localContent, field.key, arr)
        this.syncFileListMap()
      }).catch(() => {
        Toast('文件上传失败')
      })
    },
    onDelete(file, detail, field) {
      const idx = (detail && detail.index) != null ? detail.index : -1
      const current = this.localContent[field.key]
      const arr = current ? (Array.isArray(current) ? [...current] : [current]) : []
      if (idx >= 0 && idx < arr.length) arr.splice(idx, 1)
      this.$set(this.localContent, field.key, arr.length ? arr : null)
      this.syncFileListMap()
    },
    previewImage(field, idx) {
      const items = this.normalizeAttachments(this.localContent[field.key])
      const urls = items.filter(item => !this.isPdfUrl(item.url)).map(item => QNImageUtil.imageView(item.url, 0, 0))
      if (urls.length) {
        this.$emit('preview-images', { urls, index: idx })
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.dynamic-questionnaire-form {
  background: #fff;

  /* 文本类字段：标签在上、输入在下，参考金数据 */
  .field-text-block {
    padding: 12px 16px;
    border-bottom: 1px solid #ebedf0;

    .field-label-row {
      margin-bottom: 8px;
    }

    .field-label {
      font-size: 14px;
      font-weight: 600;
      color: #323233;
      line-height: 1.4;
    }

    .field-desc {
      font-size: 12px;
      color: #969799;
      margin-bottom: 8px;
    }

    .field-input-inner {
      padding: 0;

      ::v-deep .van-cell {
        padding: 10px 12px;
        background: #fff;
        border-radius: 4px;
        border: 1px solid #ebedf0;
      }

      ::v-deep .van-cell__title {
        display: none;
      }

      ::v-deep .van-cell__value {
        padding-left: 0;
      }

      ::v-deep .van-field__body {
        padding: 0;
      }

      ::v-deep .van-field__control {
        background: transparent;
      }
    }
  }

  &.readonly-form {
    ::v-deep .van-field__control {
      color: #969799;
    }
    ::v-deep .van-field__label {
      color: #969799;
    }

    .field-choice-multi .field-label,
    .field-text-block .field-label {
      color: #969799;
    }
  }
}

.field-file-upload {
  padding: 12px 16px;
  border-bottom: 1px solid #ebedf0;

  .field-label-row {
    margin-bottom: 4px;
  }

  .field-label {
    font-size: 14px;
    font-weight: 600;
    color: #323233;
  }

  .field-desc {
    font-size: 12px;
    color: #969799;
    margin-bottom: 8px;
  }

  .file-readonly-list {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    align-items: center;

    .file-link {
      color: var(--qn-theme);
      font-size: 14px;
    }

    .file-thumb {
      width: 80px;
      height: 80px;
      object-fit: cover;
      border-radius: 4px;
    }

    .text-muted {
      color: #969799;
      font-size: 13px;
    }
  }

  .upload-section {
    display: flex;
    align-items: flex-start;
  }

  .upload-placeholder {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width: 80px;
    height: 80px;
    background: #f7f8fa;
    border: 1px dashed #dcdee0;
    border-radius: 4px;
    color: #969799;
    font-size: 12px;

    .plus-icon {
      font-size: 24px;
      margin-bottom: 4px;
    }
  }
}

.field-choice-multi {
  padding: 12px 16px;
  border-bottom: 1px solid #ebedf0;

  .field-label-row {
    margin-bottom: 4px;
  }

  .field-label {
    font-size: 14px;
    font-weight: 600;
    color: #323233;
  }

  .field-desc {
    font-size: 12px;
    color: #969799;
    margin-bottom: 8px;
  }

  .choice-option-row {
    border-bottom: 1px solid #ebedf0;

    &:last-child {
      border-bottom: none;
    }
  }

  .choice-extra-input {
    padding-left: 32px;
    padding-right: 16px;
  }

  .radio-option-row {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .radio-dot {
    flex-shrink: 0;
    width: 14px;
    height: 14px;
    border-radius: 50%;
    border: 2px solid #c8c9cc;
    background: #fff;
    transition: border-color 0.2s, background 0.2s;

    &--selected {
      border-color: var(--qn-theme);
      background: var(--qn-theme);
      box-shadow: inset 0 0 0 2px #fff;
    }
  }

  .cell-readonly {
    opacity: 0.7;
    pointer-events: none;

    .van-cell__title {
      color: #969799;
    }

    .radio-dot {
      border-color: #c8c9cc;
      background: #ebedf0;

      &--selected {
        border-color: #969799;
        background: #969799;
      }
    }

    .radio-option-row span:last-child {
      color: #969799;
    }

    .van-checkbox__label {
      color: #969799;
    }
  }
}

.field-desc-inline {
  padding: 0 16px 8px;
  font-size: 12px;
  color: #969799;
}

::v-deep .van-cell__label {
  font-weight: 500;
}
</style>
